import { Shield, Users } from "lucide-react";
import { usePublicData } from "../../hooks/usePublicData";

export default function PublicTeams() {
  const { data, loading } = usePublicData();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { teams, players, standings, matches } = data;

  // Helper to get last 5 results for a team
  const getLast5Results = (teamId) => {
    const teamMatches = matches
      .filter(m => m.status === 'completed' && (m.home_team_id === teamId || m.away_team_id === teamId))
      .sort((a, b) => new Date(b.match_date) - new Date(a.match_date))
      .slice(0, 5);
    
    return teamMatches.map(match => {
      const isHome = match.home_team_id === teamId;
      const teamScore = isHome ? match.home_score : match.away_score;
      const opponentScore = isHome ? match.away_score : match.home_score;
      
      if (teamScore > opponentScore) return 'W';
      if (teamScore < opponentScore) return 'L';
      return 'D';
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
          Teams
        </h1>
        <p className="text-muted-foreground mt-1">
          {teams.length} teams competing
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map((team) => {
          const teamStats = standings.find((s) => s.team_id === team.id);
          const teamPlayers = players.filter((p) => p.team_id === team.id);
          const last5 = getLast5Results(team.id);
          
          return (
            <div
              key={team.id}
              className="bg-card border border-border rounded-xl overflow-hidden"
            >
              <div className="aspect-video bg-secondary relative flex items-center justify-center">
                {team.logo ? (
                  <img
                    src={team.logo}
                    alt={team.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Shield className="w-16 h-16 text-muted-foreground/30" />
                )}
              </div>
              <div className="p-4">
                <h3 className="font-heading text-lg font-bold uppercase mb-3">
                  {team.name}
                </h3>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-background rounded-lg p-2">
                    <div className="font-bold text-primary">
                      {teamStats?.points || 0}
                    </div>
                    <div className="text-xs text-muted-foreground">PTS</div>
                  </div>
                  <div className="bg-background rounded-lg p-2">
                    <div className="font-bold">{teamPlayers.length}</div>
                    <div className="text-xs text-muted-foreground">Players</div>
                  </div>
                  <div className="bg-background rounded-lg p-2">
                    <div className="font-bold">{teamStats?.goals_for || 0}</div>
                    <div className="text-xs text-muted-foreground">Goals</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Record</span>
                    <span>
                      <span className="text-green-500">{teamStats?.won || 0}W</span>
                      {" "}-{" "}
                      <span>{teamStats?.drawn || 0}D</span>
                      {" "}-{" "}
                      <span className="text-red-500">{teamStats?.lost || 0}L</span>
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-1">
                    <span className="text-muted-foreground">Goal Diff</span>
                    <span className={teamStats?.goal_difference > 0 ? "text-green-500" : teamStats?.goal_difference < 0 ? "text-red-500" : ""}>
                      {teamStats?.goal_difference > 0 ? "+" : ""}{teamStats?.goal_difference || 0}
                    </span>
                  </div>
                  {/* Last 5 Results */}
                  {last5.length > 0 && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <span className="text-muted-foreground text-xs">Form</span>
                      <div className="flex gap-0.5">
                        {last5.map((result, idx) => (
                          <span
                            key={idx}
                            className={`w-4 h-4 text-[9px] font-bold flex items-center justify-center rounded-sm ${
                              result === 'W' ? 'bg-green-500/20 text-green-500' :
                              result === 'L' ? 'bg-red-500/20 text-red-500' :
                              'bg-muted text-muted-foreground'
                            }`}
                          >
                            {result}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
